import React, { useCallback } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslate } from '@agilepoint/wat-react';
import { FlexBox, useNotificationEvents } from '@agilepoint/wat-react-ux';
import { StylingForm } from 'modules/shared/forms';
import { IStyleAPIData, IStylingFormFields } from 'modules/shared/types';
import { ISaveTextDataResponse } from 'modules/shared/queries';
import { getDefaultStylingFormValues, getStylingJSON, StylingResponseType } from 'modules/shared/functions';
import { TabFooterButtons } from './tab-footer-buttons';

interface IStylingProps {
    styleData: IStyleAPIData
    mutationLoading: boolean;
    onSaveStylingDetails: (args: StylingResponseType) => Promise<ISaveTextDataResponse>
}

const StylingBase = React.memo((props: IStylingProps) => {
    const { mutationLoading, onSaveStylingDetails } = props;
    const { handleSubmit } = useFormContext<IStylingFormFields>();
    const { showNotifications } = useNotificationEvents();
    const translate = useTranslate();
    const headerLabel = translate('modules.createNewApp.steps.styling.defaultStylingHeader');

    const onSubmitHandler = useCallback((formData: IStylingFormFields) => {
        onSaveStylingDetails(getStylingJSON(formData))
            .then(() => showNotifications({ message: translate('modules.createNewApp.steps.styling.styledDetailsSavedSuccessful'), type: 'success' }))
            .catch(() => showNotifications({ message: translate('modules.createNewApp.steps.styling.styledDetailsFailure'), type: 'error' }));
    }, [onSaveStylingDetails, showNotifications, translate]);

    return (
        <FlexBox flexDirection='column' height='calc(100% - 16px)' justifyContent="space-between">
            <StylingForm headerLabel={headerLabel} />
            <TabFooterButtons onSubmitClickHandler={handleSubmit(onSubmitHandler)} mutationLoading={mutationLoading} />
        </FlexBox>
    );
});

export const Styling = React.memo((props: IStylingProps) => {
    const defaultValues = getDefaultStylingFormValues(props.styleData);

    const form = useForm<IStylingFormFields>({
        mode: 'onChange',
        defaultValues
    });

    return (
        <FormProvider {...form}>
            <StylingBase {...props} />
        </FormProvider>
    );
});
